import { Button, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ProgressCard from '../../../../components/card/ProgressCard'
import { CardTable } from '../../../../components/datatable/NewTable'
import Checkbox from '../../../../components/forms/Checkbox'
import Input from '../../../../components/forms/Input'
import NumberField from '../../../../components/forms/NumberField'
import RadioButtons from '../../../../components/forms/RadioButtons'
import ReferenceLevelHelp from '../../../../components/echart/ReferenceLevelHelp'
import Select from '../../../../components/forms/Select'
import MultiContributorsAutocomplete from '../../../../referencial/components/contributor/components/MultiContributorsAutocomplete'
import { nbPerPageLabelShort, SANDRE } from '../../../../referencial/constants/ReferencialConstants'
import useSandreList from '../../../../utils/customHook/useSandreList'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../../utils/StatusUtil'
import { exportFile } from '../../../../utils/ExportDataUtil'
import { getIntegrationModes, getMeasureCoteList } from '../../../../utils/JobUtils'
import { execByType } from '../../../../utils/StationUtils'
import JobAction from '../../../actions/JobAction'
import StationAction from '../../../../station/actions/StationAction'
import Job from '../../../dto/DtoJob'
import { darkBlue } from '../../../../utils/constants/ColorTheme'
import ImportFileDialog from './dialog/ImportFileDialog'
import IntegrationModeDialog from '../IntegrationModeDialog'
import { STATION_TYPE_NAME } from '../../../../station/constants/StationConstants'
import { DefaultDialogTitle } from '../../../../components/styled/Dialog'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'

// Colonnes du tableau des paramètres, réutilisées à l'export et à l'import (même format)
const PARAM_HEADERS = ['directory', 'filter', 'station', 'dataType', 'discriminate', 'numChannel', 'codePointPrel', 'coeff', 'offset']

const ParatronicPanel = ({ job, onChangeJob, isEditMode }) => {
    const dispatch = useDispatch()
    const [openDialog, setOpenDialog] = useState(false)
    const [openImportDialog, setOpenImportDialog] = useState(false)
    const [fileContent, setFileContent] = useState(undefined)
    const [fileName, setFileName] = useState(undefined)

    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)

    const piezoNatures = useSandreList(SANDRE.PIEZOMETER_MEASURE_NATURE, true)
    const obtentionModes = useSandreList(SANDRE.OBTAINING_MODE)
    const refTemps = useSandreList(SANDRE.REF_TEMPORELLES)

    const {
        piezometryDataTypes,
        piezometers,
        pluviometers,
        pluviometryDataTypes,
        hydrometricStations,
        hydrometryDataTypes,
        contributors,
        pointsPrelByType,
    } = useSelector((store) => ({
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        piezometers: store.PiezometryReducer.piezometersLight,
        pluviometers: store.PluviometryReducer.pluviometers,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        contributors: store.ContributorReducer.contributors,
        pointsPrelByType: store.StationReducer.pointsPrelByType,
    }), shallowEqual)

    useEffect(() => {
        dispatch(JobAction.loadCSVImportPanel(p => setProgress(p), () => setDataLoaded(true)))
        // Paratronic mélange les 3 types de station : on charge les points des 3
        dispatch(StationAction.fetchStationPointsPrel(STATION_TYPE_NAME.piezometry))
        dispatch(StationAction.fetchStationPointsPrel(STATION_TYPE_NAME.hydrometry))
        dispatch(StationAction.fetchStationPointsPrel(STATION_TYPE_NAME.pluviometry))
    }, [dispatch])

    const onChangeJobParameter = (changes) => {
        onChangeJob({ parameters: { ...job.parameters, ...changes } })
    }

    const getFilters = () => {
        const newFilters = job.parameters.filters.length ? JSON.parse(job.parameters.filters[0]) : {}
        return { measureCote: 2, ...newFilters }
    }

    const onChangeFilters = (changes) => {
        const parameters = {
            ...job.parameters,
            filters: [JSON.stringify({
                ...getFilters(),
                ...changes,
            })],
        }
        onChangeJob({ parameters })
    }

    const onChangeParameters = (currentParameter, index, changes) => {
        const jobParameters = {
            ...job.parameters,
            parameters: Object.assign([], job.parameters.parameters, {
                [index]: JSON.stringify({ ...currentParameter, ...changes }),
            }),
        }
        onChangeJob({ parameters: jobParameters })
    }

    const getParametersNew = () => (job.parameters.parameters.length ? job.parameters.parameters.map(p => JSON.parse(p)) : [])

    const onDeleteParameter = (index) => {
        if (isEditMode) {
            onChangeJob({
                parameters: {
                    ...job.parameters,
                    parameters: job.parameters.parameters.filter((_, i) => i !== index),
                },
            })
        }
    }

    const onDuplicateParameter = (index) => {
        if (isEditMode) {
            onChangeJob({
                parameters: {
                    ...job.parameters,
                    parameters: [...job.parameters.parameters, job.parameters.parameters[index]],
                },
            })
        }
    }

    const addParameter = () => {
        if (isEditMode) {
            onChangeJob({
                parameters: {
                    ...job.parameters,
                    parameters: [...job.parameters.parameters, '{}'],
                },
            })
        }
    }

    const getDataTypes = (category) => execByType(category, {
        pluviometry: () => pluviometryDataTypes,
        piezometry: () => piezometryDataTypes,
        hydrometry: () => hydrometryDataTypes,
        default: () => [],
    })

    const getParametersData = (parameters) => {
        const disabled = { disabled: !isEditMode }
        const stations = [
            ...piezometers.map(s => ({ ...s, name: `[${i18n.piezometer}] ${s.code ? `[${s.code}] ${s.name}` : s.name}`, stationType: STATION_TYPE_NAME.piezometry, tmpId: `pi:${s.id}` })),
            ...pluviometers.map(s => ({ ...s, name: `[${i18n.pluviometer}] ${s.code ? `[${s.code}] ${s.name}` : s.name}`, stationType: STATION_TYPE_NAME.pluviometry, tmpId: `pl:${s.id}` })),
            ...hydrometricStations.map(s => ({ ...s, name: `[${i18n.hydrometricStation}] ${s.code ? `[${s.code}] ${s.name}` : s.name}`, stationType: STATION_TYPE_NAME.hydrometry, tmpId: `hy:${s.id}` })),
        ]
        return parameters.map((p, index) => ({
            _index: index,
            directory: <Input value={p.directory} onChange={v => onChangeParameters(p, index, { directory: v })} {...disabled} />,
            filter: <Input value={p.filter} onChange={v => onChangeParameters(p, index, { filter: v })} {...disabled} />,
            coeff: <NumberField value={p.factor} onChange={v => onChangeParameters(p, index, { factor: v })} floatValue {...disabled} />,
            offset: <NumberField value={p.offset} onChange={v => onChangeParameters(p, index, { offset: v })} floatValue {...disabled} />,
            discriminate: <NumberField value={p.discriminate} onChange={v => onChangeParameters(p, index, { discriminate: v })} floatValue {...disabled} />,
            numChannel: <NumberField value={p.numChannel} onChange={v => onChangeParameters(p, index, { numChannel: v })} floatValue {...disabled} />,
            codePointPrel: (
                <Select
                    options={(pointsPrelByType[p.stationType] || []).filter(pt => pt.idStation === p.stationId)}
                    value={p.codePointPrel}
                    keyValue='code'
                    keyLabel='name'
                    onChange={v => onChangeParameters(p, index, { codePointPrel: v })}
                    disabled={!(isEditMode && p.stationType)}
                />
            ),
            dataType: (
                <Select
                    options={getDataTypes(p.stationType)}
                    value={p.dataType}
                    onChange={v => onChangeParameters(p, index, { dataType: v })}
                    disabled={!(isEditMode && p.stationType)}
                />
            ),
            station: (
                <Select
                    options={stations}
                    onChange={(_, v) => (v && v.id ? onChangeParameters(p, index, { stationCode: v.code, stationId: v.id, stationType: v.stationType, tmpId: v.tmpId, dataType: undefined }) : null)}
                    value={p.tmpId}
                    keyValue='tmpId'
                    {...disabled}
                />
            ),
        }))
    }

    const onChangeHandledData = (data, index, newData) => {
        onChangeFilters({ handledData: data.map(d => (d.index === index ? newData : d)) })
    }

    const onDeleteHandledData = (data, index) => {
        onChangeFilters({ handledData: data.filter(d => d.index !== index) })
    }

    const buildHandledRows = (filters) => {
        const handledDataTypes = [
            { value: STATION_TYPE_NAME.pluviometry, label: i18n.pluviometry },
            { value: STATION_TYPE_NAME.hydrometry, label: i18n.hydrometry },
            { value: STATION_TYPE_NAME.piezometry, label: i18n.piezometry },
        ]
        const indexedData = (filters.handledData || []).map((d, index) => ({ ...d, index }))
        const rows = indexedData.map(d => ({
            _index: d.index,
            category: <Select options={handledDataTypes} value={d.dataCategory} onChange={v => onChangeHandledData(indexedData, d.index, { ...d, dataCategory: v })} disabled={!isEditMode} />,
            dataType: <Select options={getDataTypes(d.dataCategory)} value={d.dataType} onChange={v => onChangeHandledData(indexedData, d.index, { ...d, dataType: v })} disabled={!(isEditMode && d.dataCategory)} />,
            discriminate: <NumberField value={d.discriminate} onChange={v => onChangeHandledData(indexedData, d.index, { ...d, discriminate: v })} disabled={!isEditMode} />,
            coeff: <NumberField value={d.factor} onChange={v => onChangeHandledData(indexedData, d.index, { ...d, factor: v })} floatValue disabled={!isEditMode} />,
        }))
        return { rows, indexedData }
    }

    const exportData = (parametersList) => parametersList.map(p => {
        const param = JSON.parse(p)
        return {
            directory: param.directory,
            filter: param.filter,
            station: param.stationCode,
            dataType: param.dataType,
            coeff: param.factor,
            offset: param.offset,
            discriminate: param.discriminate,
            numChannel: param.numChannel,
            codePointPrel: param.codePointPrel,
            headers: PARAM_HEADERS,
        }
    })

    const onUploadFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        if (file) {
            reader.onloadend = (event) => {
                const split = event.target.result.split(',')
                setFileContent(split[1])
                setFileName(file.name)
            }
            reader.readAsDataURL(file)
        }
    }

    const importParams = () => {
        JobAction.jobParatronicImport(job.id, PARAM_HEADERS, fileContent, fileName)
            .then(() => dispatch(JobAction.fetchFullJob(job.id, 10)))
            .then(fullJob => {
                const parameters = JSON.parse(fullJob.parameters).parameters
                onChangeJob({ parameters: { ...job.parameters, parameters } })
                setOpenImportDialog(false)
            })
    }

    if (!dataLoaded) {
        return <ProgressCard progress={progress} />
    }

    const disabled = !isEditMode
    const filters = getFilters()
    const parameters = getParametersData(getParametersNew())
    const { rows: handledRows, indexedData: handledIndexed } = buildHandledRows(filters)
    const zipAlgorithms = [{ code: 'gzip', name: 'GZIP' }]
    const data = exportData(job.parameters.parameters)

    return (
        <>
            <Grid container rowSpacing={1} paddingTop={1}>
                <Grid size={12}>
                    <fieldset>
                        <legend>&nbsp;{i18n.defaultValue}&nbsp;</legend>
                        <Grid container className='padding-top-1' spacing={1} justifyContent='center' alignItems='center'>
                            <Grid size={2}><Select label={i18n.status} value={filters.status} onChange={v => onChangeFilters({ status: v })} options={getStatusSelectOptions()} nullLabel='&nbsp;' disabled={disabled} /></Grid>
                            <Grid size={2}><Select label={i18n.qualification} value={filters.qualification} onChange={v => onChangeFilters({ qualification: v })} options={getQualificationSelectOptions()} nullLabel='&nbsp;' disabled={disabled} /></Grid>
                            <Grid size={2}><Select label={i18n.tempRef} value={filters.tempRef} onChange={v => onChangeFilters({ tempRef: v })} options={refTemps} nullLabel='&nbsp;' disabled={disabled} /></Grid>
                            <Grid size={6}><MultiContributorsAutocomplete label={i18n.producer} values={filters.producer} onChange={v => onChangeFilters({ producer: v })} options={contributors} disabled={disabled} /></Grid>
                            <Grid size={2}><Select label={i18n.measureNature} value={filters.nature} onChange={v => onChangeFilters({ nature: v })} options={piezoNatures} nullLabel='&nbsp;' disabled={disabled} /></Grid>
                            <Grid size={2}><Select label={i18n.obtainningMode} value={filters.obtainingMode} onChange={v => onChangeFilters({ obtainingMode: v })} options={obtentionModes} nullLabel='&nbsp;' disabled={disabled} /></Grid>
                            <Grid size={4}><MultiContributorsAutocomplete label={i18n.administrator} values={filters.manager} onChange={v => onChangeFilters({ manager: v })} options={contributors} disabled={disabled} /></Grid>
                            <Grid size={4}><MultiContributorsAutocomplete label={i18n.validator} values={filters.validator} onChange={v => onChangeFilters({ validator: v })} options={contributors} disabled={disabled} /></Grid>
                        </Grid>
                    </fieldset>
                </Grid>
                <Grid container size={12} justifyContent='center'>
                    <Grid size={8}>
                        <CardTable
                            title={i18n.dataTypes}
                            color={darkBlue}
                            rows={handledRows}
                            headers={['category', 'dataType', 'discriminate', 'coeff']}
                            rowsPerPageOptions={nbPerPageLabelShort}
                            lineActions={[
                                {
                                    icon: 'close',
                                    tooltip: i18n.delete,
                                    onClick: row => {
                                        onDeleteHandledData(handledIndexed, row._index)
                                    },
                                    displayed: isEditMode,
                                },
                            ]}
                            actions={[
                                {
                                    icon: 'note_add',
                                    tooltip: i18n.add,
                                    onClick: () => onChangeFilters({ handledData: [...(filters.handledData || []), {}] }),
                                    displayed: isEditMode,
                                    color: 'white',
                                },
                            ]}
                            hideNbElements
                        />
                    </Grid>
                </Grid>
                <Grid container size={12} spacing={1} alignItems='center'>
                    <Grid size={3}>
                        <Checkbox
                            label={i18n.renameDoneErrorFic}
                            onChange={v => onChangeFilters(v ? { renameProcessedFiles: true, dontProcessFilesInLogs: false } : { renameProcessedFiles: false })}
                            disabled={disabled}
                            checked={filters.renameProcessedFiles}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Checkbox
                            label={i18n.dontProcessFilesInLogs}
                            onChange={v => onChangeFilters(v ? { dontProcessFilesInLogs: true, renameProcessedFiles: false } : { dontProcessFilesInLogs: false })}
                            disabled={disabled}
                            checked={filters.dontProcessFilesInLogs}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Checkbox
                            label={i18n.replayErrorFiles}
                            onChange={v => onChangeFilters({ replayErrorFiles: v })}
                            disabled={disabled}
                            checked={filters.replayErrorFiles}
                        />
                    </Grid>
                    <Grid size={2}>
                        <Select
                            options={zipAlgorithms}
                            label='Compression'
                            onChange={v => onChangeFilters({ zipFile: v })}
                            value={filters.zipFile}
                        />
                    </Grid>
                    <Grid size={3}>
                        <RadioButtons
                            elements={getMeasureCoteList()}
                            selected={filters.measureCote}
                            onChange={v => onChangeFilters({ measureCote: v })}
                            title={i18n.ratingExpression}
                            titleAction={<ReferenceLevelHelp stationType={STATION_TYPE_NAME.piezometry} />}
                            disabled={disabled}
                        />
                    </Grid>
                </Grid>
                <Grid container size={12} spacing={1} alignItems='center'>
                    <Grid size={4}>
                        <Input
                            title={i18n.directory}
                            value={job.parameters.path}
                            onChange={v => onChangeJobParameter({ path: v })}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid size='auto'>
                        <a className='btn btn-small secondary-color-back' onClick={() => setOpenDialog(true)}>
                            <span title={i18n.importLabelFtp}>
                                <i className='material-icons'>input</i>
                            </span>
                        </a>
                    </Grid>
                    <Grid size={3}>
                        <NumberField
                            title={i18n.excludedValue}
                            value={filters.excludedValue}
                            onChange={v => onChangeFilters({ excludedValue: v })}
                            disabled={disabled}
                            floatValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            options={getIntegrationModes()}
                            label={i18n.dataIntegration}
                            value={filters.importMode}
                            labelSpan={<IntegrationModeDialog />}
                            onChange={v => onChangeFilters({ importMode: v })}
                            disabled={disabled}
                        />
                    </Grid>
                </Grid>
                <Grid size={12} className='job-parameter-table'>
                    <CardTable
                        title={i18n.parameters}
                        rows={parameters}
                        headers={['directory', 'filter', 'station', 'dataType', 'discriminate', { key: 'numChannel', value: i18n.pathNumber }, { key: 'codePointPrel', value: `code ${i18n.pointPrelevement}` }, 'coeff', 'offset']}
                        rowsPerPageOptions={nbPerPageLabelShort}
                        color={darkBlue}
                        lineActions={[
                            { icon: 'delete', tooltip: i18n.delete, onClick: row => onDeleteParameter(row._index), displayed: isEditMode },
                            { icon: 'add_to_photos', tooltip: i18n.duplicate, onClick: row => onDuplicateParameter(row._index), displayed: isEditMode },
                        ]}
                        actions={[
                            { icon: 'add_box', tooltip: i18n.add, onClick: addParameter, displayed: isEditMode, color: 'white' },
                            { icon: 'file_upload', tooltip: i18n.importFile, onClick: () => setOpenImportDialog(true), displayed: isEditMode, color: 'white' },
                            { icon: 'download', tooltip: i18n.download, onClick: () => exportFile({ data, titleFile: i18n.parameters }), color: 'white' },
                        ]}
                    />
                </Grid>

            </Grid>
            <ImportFileDialog
                open={openDialog}
                setOpen={setOpenDialog}
                url={job.parameters.path}
            />
            <Dialog maxWidth='lg' open={openImportDialog}>
                <DefaultDialogTitle
                    title={i18n.importParameters}
                    onClose={() => setOpenImportDialog(false)}
                />
                <DialogContent>
                    <Grid size={12} paddingTop={1}>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.fileLabel}</StyledLegend>
                            <Typography>{i18n.pleaseUseSameFileExmport}</Typography>
                            <div className='file-field input-field'>
                                <div className='btn'>
                                    <span>{i18n.select}</span>
                                    <input type='file' onChange={onUploadFile} />
                                </div>
                                <div className='file-path-wrapper'>
                                    <input className='file-path validate' type='text' />
                                </div>
                            </div>
                        </StyledFieldSet>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={importParams}>
                        {i18n.importLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

ParatronicPanel.propTypes = {
    job: PropTypes.instanceOf(Job).isRequired,
    onChangeJob: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
}

export default ParatronicPanel
